'use client';

/**
 * RankingField — Drag-to-rank items in order.
 *
 * Config via field.options (array of { value, label }) or field.metadata.items.
 * Value stored as string[] — ordered item values from top (rank 1) to bottom.
 *
 * Uses native drag & drop (HTML5) — no extra dependencies.
 */

import React, { useState, useRef } from 'react';
import { GripVertical } from 'lucide-react';

import type { FieldComponentField, FieldValue } from '../formFieldTypes';
import { asOptionArray } from '../formFieldTypes';

interface RankingFieldProps {
  field: FieldComponentField;
  value: FieldValue;
  onChange: (value: string[]) => void;
  isDark: boolean;
  readOnly?: boolean;
}

export const RankingField: React.FC<RankingFieldProps> = ({
  field, value, onChange, isDark, readOnly,
}) => {
  const meta = field.metadata as { items?: { value: string; label: string }[] } | undefined;
  const items: { value: string; label: string }[] =
    meta?.items || asOptionArray(field.options);

  // Current order — initialise from value or default item order
  const ordered: string[] = Array.isArray(value) && value.length === items.length
    ? (value as string[])
    : items.map((i) => i.value);

  const labelMap = Object.fromEntries(items.map((i) => [i.value, i.label]));

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const dragRef = useRef<number | null>(null);

  const handleDragStart = (idx: number) => {
    dragRef.current = idx;
    setDragIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setOverIdx(idx);
  };

  const handleDrop = (idx: number) => {
    const from = dragRef.current;
    if (from === null || from === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  // Fallback: move up / move down buttons for mobile
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= ordered.length) return;
    const next = [...ordered];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  if (!items.length) {
    return (
      <div className={`text-sm p-3 rounded ${isDark ? 'bg-yellow-900/20 text-yellow-200' : 'bg-yellow-50 text-yellow-700'}`}>
        Ranking field &quot;{field.label}&quot; has no items configured.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {ordered.map((key, idx) => (
        <div
          key={key}
          draggable={!readOnly}
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          onDrop={() => handleDrop(idx)}
          className={`
            flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all
            ${readOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
            ${dragIdx === idx ? 'opacity-40' : ''}
            ${overIdx === idx && dragIdx !== idx ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}
            ${isDark
              ? 'bg-gray-800 border-gray-700 text-gray-200'
              : 'bg-white border-gray-200 text-gray-800 shadow-sm'
            }
          `}
        >
          {!readOnly && (
            <GripVertical className="w-4 h-4 flex-shrink-0 opacity-40" />
          )}
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
          }`}>
            {idx + 1}
          </span>
          <span className="flex-1 truncate font-medium">
            {labelMap[key] || key}
          </span>
          {!readOnly && (
            <div className="flex gap-0.5">
              <button
                type="button"
                disabled={idx === 0}
                onClick={() => move(idx, -1)}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-20"
                aria-label="Move up"
              >
                ▲
              </button>
              <button
                type="button"
                disabled={idx === ordered.length - 1}
                onClick={() => move(idx, 1)}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-20"
                aria-label="Move down"
              >
                ▼
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
